import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { login, fetchUserInfo, logout } from "store/slices/userSlice";
import LoginTemplate from "components/templates/LoginTemplate";
import { LoginRequest } from "types/auth";
import { log, LogLevel } from "utils/LogUtil";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { startKakaoAuthorize } from "utils/kakaoAuth";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import { COLOR_SYSTEM } from "components/design/design-system";

// location.state 타입 정의
interface LocationState {
  from?: {
    pathname: string;
  };
}

// 쿠키 삭제 함수
const deleteAuthCookies = () => {
  // 모든 가능한 인증 관련 쿠키를 삭제합니다.
  // path와 domain을 지정하지 않으면 현재 경로와 도메인에 있는 쿠키만 삭제됩니다.
  // 따라서 여러 조합을 시도하여 모든 가능한 쿠키를 삭제합니다.

  // 알려진 쿠키 이름들 (더 많은 이름을 추가할 수 있음)
  const cookieNames = [
    "prai-id-token",
    "prai-refresh-token",
    "auth-token",
    "access_token",
    "refresh_token",
  ];

  // 루트 경로와 현재 경로에 대해 쿠키 삭제 시도
  const paths = ["/", ""];

  // 각 쿠키 이름과 경로 조합에 대해 삭제 시도
  cookieNames.forEach((name) => {
    paths.forEach((path) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;
    });
  });

  log(LogLevel.INFO, "인증 관련 쿠키 삭제 시도 완료");
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // location.state에서 from 정보 가져오기
  const state = location.state as LocationState;
  // state 또는 localStorage에서 리디렉션 경로 확인
  const fromPathname = state?.from?.pathname;
  const storedRedirect = localStorage.getItem("authRedirect");
  const from = fromPathname || storedRedirect || "/dashboard";

  const { isLoading, error, isAuthenticated } = useSelector((state: RootState) => state.user);

  const [credentials, setCredentials] = useState<LoginRequest>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  // 로그인 페이지 진입 시 무한 리다이렉트 문제 해결을 위한 처리
  useEffect(() => {
    // 쿠키는 있지만 인증이 실패하는 경우를 처리하기 위해
    // 로그인 페이지에 접근했을 때 인증 상태를 초기화하고 쿠키를 삭제합니다
    if (document.cookie.includes("prai-id-token") || document.cookie.includes("auth")) {
      log(LogLevel.INFO, "로그인 페이지 진입: 유효하지 않은 쿠키 감지, 세션 초기화 시도");

      // 백엔드에 로그아웃 요청을 보냅니다 (실패하더라도 계속 진행)
      dispatch(logout()).catch((err) => log(LogLevel.ERROR, "로그아웃 실패:", err));

      // 쿠키 삭제
      deleteAuthCookies();
    }
  }, [dispatch]);

  // 이미 로그인된 사용자가 로그인 페이지에 접근할 경우 리다이렉트
  useEffect(() => {
    if (isAuthenticated) {
      log(LogLevel.INFO, "이미 로그인된 사용자 감지: 메인 페이지로 리다이렉트");
      localStorage.removeItem("authRedirect"); // 사용 후 삭제
      navigate(from);
    }
  }, [isAuthenticated, navigate, from]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCredentials((prev) => ({ ...prev, email: value }));

    // Clear previous error when user starts typing
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCredentials((prev) => ({ ...prev, password: value }));

    // Clear previous error when user starts typing
    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!credentials.email) {
      newErrors.email = "이메일을 입력해주세요.";
    } else if (!validateEmail(credentials.email)) {
      newErrors.email = "유효한 이메일 형식이 아닙니다.";
    }

    if (!credentials.password) {
      newErrors.password = "비밀번호를 입력해주세요.";
    } else if (credentials.password.length < 8) {
      newErrors.password = "비밀번호는 최소 8자 이상이어야 합니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Redux 액션을 통해 로그인 요청 디스패치
      const resultAction = await dispatch(login(credentials));

      if (login.fulfilled.match(resultAction)) {
        // 로그인 성공 후 사용자 정보 가져오기
        await dispatch(fetchUserInfo());

        // 로그인 성공 시 처리
        log(LogLevel.INFO, "로그인 성공:", resultAction.payload);
        // 리다이렉트는 useEffect(isAuthenticated)에서 처리
      }
    } catch (error) {
      log(LogLevel.ERROR, "로그인 중 오류 발생:", error);
    }
  };

  const handleSignUp = () => {
    ConfirmManager.show({
      title: "PhAI 회원가입",
      body: (
        <div style={{ textAlign: "center", lineHeight: "1.8" }}>
          <p style={{ fontWeight: "600", fontSize: "18px", marginBottom: "8px" }}>
            약문약답 회원이신가요?
          </p>
          <p style={{ fontSize: "14px", color: COLOR_SYSTEM.get("Gray")[500], marginBottom: "24px" }}>
            PhAI는 약사 전용 처방 검토 서비스로,<br />
            약문약답 회원 확인을 통해 약사 인증을 진행합니다.
          </p>
        </div>
      ),
      buttons: [
        <div key="all-content" style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div key="button-container" style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
            <Button
              key="yes-ymyd"
              type="Text"
              size="Medium"
              variant="Contained"
              color="Primary"
              style={{
                backgroundColor: COLOR_SYSTEM.get("Yellow")[400],
                color: COLOR_SYSTEM.get("Gray")[900],
                border: "none",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px"
              }}
              onClick={() => {
                ConfirmManager.hide();
                startKakaoAuthorize("signup-ymyd");
              }}
        >
          <Icon name="Kakao" size={20} />
          예, 약문약답 회원입니다
        </Button>
        <Button
          key="no-ymyd"
          type="Text"
          size="Medium"
          variant="Outlined"
          color="Secondary"
          style={{ width: "100%" }}
          onClick={() => {
            ConfirmManager.hide();
            // 약문약답 미가입자 안내 팝업 표시
            ConfirmManager.show({
              title: "회원가입 절차 안내",
              body: (
                <div style={{ textAlign: "left", lineHeight: "1.6" }}>
                  <p style={{ marginBottom: "16px", fontSize: "15px" }}>
                    먼저 약문약답 가입이 필요합니다.
                  </p>

                  <div style={{
                    backgroundColor: COLOR_SYSTEM.get("Gray")[10],
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "16px"
                  }}>




                    {/* 1단계 */}
                    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "16px" }}>
                      <div style={{
                        backgroundColor: COLOR_SYSTEM.get("Blue")[400],
                        color: "white",
                        borderRadius: "26%",
                        width: "36px",
                        height: "25px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginRight: "12px",
                        fontSize: "12px",
                        fontWeight: "bold"
                      }}>
                        1단계
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: "14px", color: COLOR_SYSTEM.get("Gray")[700], fontWeight: "500" }}>
                          약문약답 가입
                        </p>
                        <p style={{ margin: "2px 0 8px 0", fontSize: "13px", color: COLOR_SYSTEM.get("Gray")[500] }}>
                          아래 버튼을 눌러 약문약답을 가입하고 승인을 기다려주세요
                        </p>
                        <p style={{ margin: "0", fontSize: "12px", color: COLOR_SYSTEM.get("Orange")[500] }}>
                          <strong>준비물:</strong> 약사 면허증 사진
                        </p>
                      </div>
                    </div>

                    {/* 2단계 */}
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <div style={{
                        backgroundColor: COLOR_SYSTEM.get("Blue")[400],
                        color: "white",
                        borderRadius: "26%",
                        width: "36px",
                        height: "25px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginRight: "12px",
                        fontSize: "12px",
                        fontWeight: "bold"
                      }}>
                        2단계
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: "14px", color: COLOR_SYSTEM.get("Gray")[700], fontWeight: "500" }}>
                          PhAI 회원가입
                        </p>
                        <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: COLOR_SYSTEM.get("Gray")[500] }}>
                          약문약답 회원가입이 승인되고 나면<br />
                          첫 화면으로 돌아가서 PhAI 회원가입을 완료해주세요.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ),
              buttons: [
                <Button
                  key="go-to-ymyd"
                  type="Text"
                  size="Medium"
                  variant="Contained"
                  color="Primary"
                  style={{ width: "100%" }}
                  onClick={() => {
                    window.open("https://app.stg.ymyd.onjourney.co.kr/", "_blank");
                    ConfirmManager.hide();
                  }}
                >
                  약문약답 가입하러 가기
                </Button>,
                <Button
                  key="close"
                  type="Text"
                  size="Medium"
                  variant="Outlined"
                  color="Secondary"
                  style={{ width: "100%" }}
                  onClick={() => {
                    ConfirmManager.hide();
                  }}
                >
                  닫기
                </Button>
              ]
            });
          }}
        >
          아니오, 약문약답 회원이 아닙니다
        </Button>
            <Button
              key="cancel"
              type="Text"
              size="Medium"
              variant="Outlined"
              color="Secondary"
              style={{ width: "100%", marginTop: "8px" }}
              onClick={() => {
                ConfirmManager.hide();
              }}
            >
              닫기
            </Button>
          </div>
          <div key="description" style={{
            marginTop: "20px",
            padding: "16px",
            backgroundColor: COLOR_SYSTEM.get("Gray")[10],
            borderRadius: "8px",
            fontSize: "13px",
            color: COLOR_SYSTEM.get("Gray")[500],
            lineHeight: "1.6"
          }}>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>약문약답</strong>은 약사 전용 커뮤니티(30,000명+)로,<br />
              가입 시 약사 면허증을 제출하여 1:1 검증 후 승인됩니다.
            </p>
            <p style={{ margin: 0, color: COLOR_SYSTEM.get("Red")[500], fontSize: "12px" }}>
              ※ 약대 학생 회원은 가입할 수 없습니다
            </p>
          </div>
        </div>
      ]
    });
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  // 로딩 중이면 로딩 표시
  if (isLoading) {
    return <div>로딩 중...</div>;
  }

  return (
    <LoginTemplate
      email={credentials.email}
      password={credentials.password}
      isLoading={isLoading}
      emailError={errors.email}
      passwordError={
        errors.password ||
        (error ? (typeof error === "string" ? error : "로그인에 실패했습니다.") : null)
      }
      onEmailChange={handleEmailChange}
      onPasswordChange={handlePasswordChange}
      onSubmit={handleSubmit}
      onSignUp={handleSignUp}
      onForgotPassword={handleForgotPassword}
    />
  );
};

export default Login;